import { useMutation, useQuery } from "@apollo/client";
import {
  GetAllOrganizationTeamsQueryQuery,
  GetAllOrganizationTeamsQueryQueryVariables,
  UpdateMemberMutationMutation,
  UpdateMemberMutationMutationVariables,
} from "types/graphql-schema";

import getAllOrganizationTeamsQuery from "@apps/artifact/graphql/get-all-organization-teams-query";
import getLoggedInUserQuery from "@apps/main/graphql/get-logged-in-user-query";
import TeamPicker from "@apps/reporting/components/team-picker";
import useLabel from "@apps/use-label/use-label";
import { ButtonSize } from "@components/button/button-constants";
import { assertEdgesNonNull } from "@helpers/helpers";

import getOrganizationTeamsQuery from "../graphql/get-organization-teams-query";
import updateMemberMutation from "../graphql/update-member-mutation";

// The departments this person leads, whatever their role: their career tracks, and the
// competencies that reach only them, are the lead's to edit. Each change
// saves straight away, and sends only the department added or removed: the
// list here can be older than a lead set on the department page.
const MemberLedDepartments = ({
  organizationId,
  userId,
  ledTeamIds,
}: {
  organizationId: number;
  userId: number;
  ledTeamIds: readonly number[];
}) => {
  const label = useLabel();
  const { data } = useQuery<
    GetAllOrganizationTeamsQueryQuery,
    GetAllOrganizationTeamsQueryQueryVariables
  >(getAllOrganizationTeamsQuery, { variables: { organizationId } });
  const [updateMember, { loading: isSaving }] = useMutation<
    UpdateMemberMutationMutation,
    UpdateMemberMutationMutationVariables
  >(updateMemberMutation);

  const departments = data?.organization
    ? assertEdgesNonNull(data.organization.teams)
    : [];
  const ledDepartments = departments.filter(({ id }) =>
    ledTeamIds.includes(id),
  );
  const save = (
    variables: Pick<
      UpdateMemberMutationMutationVariables,
      "addLedTeamIds" | "removeLedTeamIds"
    >,
  ) =>
    updateMember({
      variables: { organizationId, userId, ...variables },
      context: {
        errorMatches: [
          {
            match: "Only an active member can lead",
            title: `Only an active member can lead a ${label("team")}`,
            description: `Reactivate this person first, then pick the ${label("team", { pluralize: true })} they lead.`,
          },
        ],
      },
      // The department page shows each department's leads, and the
      // logged-in user's own departments come from getLoggedInUserQuery.
      refetchQueries: [getLoggedInUserQuery, getOrganizationTeamsQuery],
      // The error link shows the toast. Passing onError also stops the call
      // rejecting with nobody to catch it.
      onError: () => {},
    });

  return (
    <TeamPicker
      size={ButtonSize.medium}
      canChange={!isSaving}
      teams={ledDepartments}
      // The member's organization, which may not be the one selected.
      organizationId={organizationId}
      onAddTeam={(team) => save({ addLedTeamIds: [team.id] })}
      onRemoveTeam={(team) => save({ removeLedTeamIds: [team.id] })}
    />
  );
};

export default MemberLedDepartments;
